import type { SeoMetadata } from '@/seo/Seo';

const platformDescription =
  'Nang Book is a cloud-based hospitality management platform for hotels, guest houses, lodges, and short-stay properties.';

export const pageSeo = {
  home: {
    title: 'Nang Book — Manage every stay from one secure workspace.',
    description: platformDescription,
    canonicalPath: '/',
  },
  comingSoon: {
    title: 'Coming Soon | Nang Book',
    description: 'Something new is on the way for Nang Book. Leave your email to be notified the moment it launches.',
    canonicalPath: '/coming-soon',
    noIndex: true,
  },
  notFound: {
    title: 'Page Not Found | Nang Book',
    description: 'The page you were looking for could not be found.',
    noIndex: true,
  },
  privacyPolicy: {
    title: 'Privacy Policy | Nang Book',
    description: 'Read how Nang Book handles account, property, guest, booking, payment, and usage information.',
    canonicalPath: '/privacy-policy',
  },
  termsAndCondition: {
    title: 'Terms and Condition | Nang Book',
    description: 'Review the baseline terms for using Nang Book hospitality management workflows.',
    canonicalPath: '/terms-and-condition',
  },
  register: {
    title: 'Register | Nang Book',
    description: 'Verify your email with a one-time code, then create a Nang Book account.',
    canonicalPath: '/register',
    noIndex: true,
  },
  onboarding: {
    title: 'Get Started | Nang Book',
    description: 'Create a property or wait for a staff invitation to join one.',
    canonicalPath: '/onboarding',
    noIndex: true,
  },
  propertyCreate: {
    title: 'Create Property | Nang Book',
    description: 'Set up a new Nang Book property workspace.',
    canonicalPath: '/properties/new',
    noIndex: true,
  },
  login: {
    title: 'Login | Nang Book',
    description: 'Sign in to a Nang Book hospitality management workspace.',
    canonicalPath: '/login',
    noIndex: true,
  },
  forgotPassword: {
    title: 'Forgot Password | Nang Book',
    description: 'Request a secure Nang Book password reset link for a staff account.',
    canonicalPath: '/forgot-password',
    noIndex: true,
  },
  resendPasswordReset: {
    title: 'Resend Password Reset Link | Nang Book',
    description: 'Send another Nang Book password reset email to a staff account.',
    canonicalPath: '/forgot-password/resend',
    noIndex: true,
  },
  resetPassword: {
    title: 'Reset Password | Nang Book',
    description: 'Set a new Nang Book account password from a secure reset email.',
    canonicalPath: '/reset-password',
    noIndex: true,
  },
  verifyEmail: {
    title: 'Verify Email | Nang Book',
    description: 'Confirm an administrator email address before using a Nang Book workspace.',
    canonicalPath: '/verify-email',
    noIndex: true,
  },
  resendVerification: {
    title: 'Resend Verification Email | Nang Book',
    description: 'Send another Nang Book email verification link to a property administrator.',
    canonicalPath: '/verify-email/resend',
    noIndex: true,
  },
  profile: {
    title: 'My Profile | Nang Book',
    description: 'Update your Nang Book account details and password.',
    canonicalPath: '/profile',
    noIndex: true,
  },
  dashboard: {
    title: 'Dashboard | Nang Book',
    description: 'Private Nang Book operations dashboard for property teams.',
    canonicalPath: '/app',
    noIndex: true,
  },
  guests: {
    title: 'Guest Records | Nang Book',
    description: 'Private Nang Book guest records and contact history workspace.',
    canonicalPath: '/guests',
    noIndex: true,
  },
  guestNew: {
    title: 'Add Guest | Nang Book',
    description: 'Create a new Nang Book guest record.',
    canonicalPath: '/guests/new',
    noIndex: true,
  },
  guestDetail: {
    title: 'Guest Profile | Nang Book',
    description: 'Private Nang Book guest profile, booking history, and identification documents.',
    canonicalPath: '/guests',
    noIndex: true,
  },
  bookings: {
    title: 'Bookings | Nang Book',
    description: 'Private Nang Book booking records, filterable by date, channel, status, and purpose.',
    canonicalPath: '/bookings',
    noIndex: true,
  },
  bookingNew: {
    title: 'Add Booking | Nang Book',
    description: 'Create a new Nang Book booking.',
    canonicalPath: '/bookings/new',
    noIndex: true,
  },
  bookingDetail: {
    title: 'Booking Details | Nang Book',
    description: 'Private Nang Book booking details, financial record, review, and comments.',
    canonicalPath: '/bookings',
    noIndex: true,
  },
  bookingChannels: {
    title: 'Booking Channels | Nang Book',
    description: 'Manage the booking channels available to a Nang Book property workspace.',
    canonicalPath: '/settings/booking-channels',
    noIndex: true,
  },
  rooms: {
    title: 'Rooms | Nang Book',
    description: 'Manage Nang Book room inventory, status, capacity, and rate records.',
    canonicalPath: '/settings/rooms',
    noIndex: true,
  },
  roomDetail: {
    title: 'Room Schedule | Nang Book',
    description: 'Room occupancy calendar, upcoming bookings, and next availability.',
    canonicalPath: '/settings/rooms',
    noIndex: true,
  },
  reports: {
    title: 'Reports | Nang Book',
    description: 'Operational reports drawn from a Nang Book workspace\'s booking, financial, and review records.',
    canonicalPath: '/reports',
    noIndex: true,
  },
  auditLog: {
    title: 'Audit Log | Nang Book',
    description: 'Every write action taken in a Nang Book workspace, restricted to System Administrators.',
    canonicalPath: '/audit-log',
    noIndex: true,
  },
  notifications: {
    title: 'Notifications | Nang Book',
    description: 'Booking, payment and financial activity alerts for a Nang Book workspace.',
    canonicalPath: '/notifications',
    noIndex: true,
  },
  propertyProfile: {
    title: 'Property Profile | Nang Book',
    description: 'Self-service account and contact details for a Nang Book property workspace.',
    canonicalPath: '/settings/property',
    noIndex: true,
  },
  verifyReceipt: {
    title: 'Verify Receipt | Nang Book',
    description: 'Authenticate a Nang Book receipt by its number.',
    canonicalPath: '/verify-receipt',
    noIndex: true,
  },
  propertyMembers: {
    title: 'Members & Invitations | Nang Book',
    description: 'Manage staff members and pending invitations for a Nang Book property.',
    canonicalPath: '/settings/members',
    noIndex: true,
  },
  invitationAccept: {
    title: 'Accept Invitation | Nang Book',
    description: 'Accept a staff invitation to join a Nang Book property workspace.',
    canonicalPath: '/invitations',
    noIndex: true,
  },
  platformProperties: {
    title: 'Platform Properties | Nang Book',
    description: 'Cross-tenant account oversight for Nang Book platform administrators.',
    canonicalPath: '/platform/properties',
    noIndex: true,
  },
  platformPropertyDetail: {
    title: 'Property Details | Nang Book',
    description: 'Cross-tenant property account details for Nang Book platform administrators.',
    canonicalPath: '/platform/properties',
    noIndex: true,
  },
  publicBookingLink: {
    title: 'Your Booking | Nang Book',
    description: 'View your booking details or leave a review through a secure Nang Book guest link.',
    canonicalPath: '/booking-links',
    noIndex: true,
  },
  cleanup: {
    title: 'Data Cleanup | Nang Book',
    description: 'Bulk-correct guest and booking records flagged as missing required information.',
    canonicalPath: '/cleanup',
    noIndex: true,
  },
} satisfies Record<string, SeoMetadata>;
